/**
 * HUD Manager for Kobayashi Maru
 * Manages all in-game HUD elements including wave info, resources, score, and Kobayashi Maru status
 */
import { Application, Container, Graphics } from 'pixi.js';
import { UI_STYLES } from './styles';
import { UI_CONFIG } from '../config';
import { HUDData } from './types';

import { GAME_CONFIG } from '../types/constants';
import { TurretMenu } from './TurretMenu';
import { TurretUpgradePanel } from './TurretUpgradePanel';
import { MobileControlsOverlay } from './MobileControlsOverlay';
import { MessageLog } from './MessageLog';
import { AudioManager } from '../audio';
import { ResponsiveUIManager } from './ResponsiveUIManager';
import { HUDLayoutManager } from './HUDLayoutManager';
import { WavePanel, ResourcePanel, StatusPanel, CombatStatsPanel, ScorePanel, TurretCountPanel, AIPanel, AIThoughtFeed, ComboPanel } from './panels';
import { ToggleButton, IconButton } from './components';
import { AIBrainRenderer } from '../ai/visualization';
import { WaveAnnouncement } from './overlays/WaveAnnouncement';
import { AlertStatusOverlay } from './overlays/AlertStatusOverlay';
import { UIAnimator } from './animation/UIAnimator';
import type { AIStatusExtended, ThreatVector, SectorData } from '../ai/types';
import type { AIMessage } from '../ai/humanization/AIMessageGenerator';

// Forward declaration for Game type to avoid circular imports
export interface HUDCallbacks {
  onToggleGodMode: () => void;
  onToggleSlowMode: () => void;
  onToggleAI?: () => void;
  onToggleAIBrain?: () => void;
}

/**
 * HUDManager class - manages all HUD display elements
 */
export class HUDManager {
  public container: Container;
  private visible: boolean = true;
  private app: Application | null = null;
  private callbacks: HUDCallbacks | null = null;
  private responsiveUIManager: ResponsiveUIManager | null = null;
  private layoutManager: HUDLayoutManager | null = null;

  // Local state for cheat modes
  private godModeEnabled: boolean = false;
  private slowModeEnabled: boolean = false;

  // UI Panels
  private wavePanel: WavePanel | null = null;
  private resourcePanel: ResourcePanel | null = null;
  private statusPanel: StatusPanel | null = null;
  private scorePanel: ScorePanel | null = null;
  private turretCountPanel: TurretCountPanel | null = null;
  private combatStatsPanel: CombatStatsPanel | null = null;
  private comboPanel: ComboPanel | null = null;

  // UI Components
  private turretMenu: TurretMenu | null = null;
  private turretUpgradePanel: TurretUpgradePanel | null = null;
  private mobileControls: MobileControlsOverlay | null = null;
  private messageLog: MessageLog | null = null;

  // AI HUD engagement components
  private aiPanel: AIPanel | null = null;
  private aiThoughtFeed: AIThoughtFeed | null = null;

  // Sound mute button (using IconButton component)
  private muteButton: IconButton | null = null;

  // Toggle buttons
  private godModeButton: ToggleButton | null = null;
  private slowModeButton: ToggleButton | null = null;
  private aiButton: ToggleButton | null = null;
  private aiBrainButton: ToggleButton | null = null;

  // AI Brain visualization
  private aiBrainRenderer: AIBrainRenderer | null = null;
  private aiBrainEnabled: boolean = false;

  // AI state
  private aiEnabled: boolean = false;

  // Wave announcement overlay
  private waveAnnouncement: WaveAnnouncement | null = null;

  // Alert status overlay
  private alertStatusOverlay: AlertStatusOverlay | null = null;

  // Bound event handler for cleanup
  private boundResizeHandler: (() => void) | null = null;

  constructor() {
    this.container = new Container();
  }

  /**
   * Initialize the HUD with PixiJS Application
   * @param app - PixiJS Application instance
   * @param callbacks - Optional callbacks for HUD actions
   */
  init(app: Application, callbacks?: HUDCallbacks): void {
    this.app = app;
    this.callbacks = callbacks ?? null;
    this.responsiveUIManager = new ResponsiveUIManager();
    this.layoutManager = new HUDLayoutManager();

    // Add HUD container to stage (on top of everything)
    this.app.stage.addChild(this.container);

    // Create all HUD elements
    this.createTopLeftPanel();
    this.createTopRightPanel();
    this.createBottomLeftPanel();
    this.createBottomCenterPanel();
    this.createBottomRightPanel();
    this.createMuteButton();
    this.createStatsPanel();
    this.createGodModeButton();
    this.createSlowModeButton();
    this.createAIButton();
    this.createAIBrainButton();
    this.createAIPanel();

    // Create Turret Menu
    this.turretMenu = new TurretMenu();
    const menuX = GAME_CONFIG.WORLD_WIDTH - UI_CONFIG.PANELS.TURRET_MENU.WIDTH - UI_STYLES.PADDING;
    const menuY = UI_STYLES.PADDING + UI_CONFIG.LEFT_COLUMN.RESOURCE_HEIGHT_OFFSET + UI_STYLES.PADDING;
    this.turretMenu.setPosition(menuX, menuY);
    this.container.addChild(this.turretMenu.container);

    // Create Turret Upgrade Panel (hidden by default)
    this.turretUpgradePanel = new TurretUpgradePanel();
    const upgradePanelX = menuX - UI_CONFIG.PANELS.UPGRADE.WIDTH - UI_STYLES.PADDING;
    const upgradePanelY = menuY;
    this.turretUpgradePanel.setPosition(upgradePanelX, upgradePanelY);
    this.container.addChild(this.turretUpgradePanel.container);

    // Create Mobile Controls Overlay
    this.mobileControls = new MobileControlsOverlay();
    this.container.addChild(this.mobileControls.container);

    // Create Message Log
    this.messageLog = new MessageLog();
    this.messageLog.setPosition(UI_STYLES.PADDING, GAME_CONFIG.WORLD_HEIGHT - UI_CONFIG.MESSAGE_LOG.BOTTOM_OFFSET);
    this.container.addChild(this.messageLog.container);

    // Create Combo Panel (self-manages via EventBus, HUDManager drives update)
    this.comboPanel = new ComboPanel();
    this.comboPanel.init(this.container);
    // Position near bottom-left, above score panel
    const comboDims = ComboPanel.getDimensions();
    this.comboPanel.setPosition(
      UI_STYLES.PADDING,
      GAME_CONFIG.WORLD_HEIGHT - UI_CONFIG.PANELS.SCORE.HEIGHT - UI_STYLES.PADDING - comboDims.height - UI_STYLES.PADDING
    );

    // Create Wave Announcement overlay
    this.waveAnnouncement = new WaveAnnouncement();
    this.waveAnnouncement.init(this.container);

    // Create Alert Status overlay
    this.alertStatusOverlay = new AlertStatusOverlay();
    this.alertStatusOverlay.init(this.container);

    // Handle resize
    this.boundResizeHandler = this.handleResize.bind(this);
    window.addEventListener('resize', this.boundResizeHandler);
    this.handleResize();
  }

  /**
   * Handle window resize to update HUD layout
   */
  private handleResize(): void {
    if (!this.responsiveUIManager || !this.layoutManager) return;

    const scale = this.responsiveUIManager.getScaleFactor();
    const layout = this.layoutManager;

    // Update Wave Panel (top-left)
    if (this.wavePanel) {
      this.wavePanel.setScale(scale);
      const pos = layout.getWavePanelPosition(scale);
      this.wavePanel.setPosition(pos.x, pos.y);
    }

    // Update Resource Panel (top-right)
    if (this.resourcePanel) {
      this.resourcePanel.setScale(scale);
      const pos = layout.getResourcePanelPosition(scale, UI_CONFIG.PANELS.RESOURCE.WIDTH);
      this.resourcePanel.setPosition(pos.x, pos.y);
    }

    // Update Score Panel (bottom-left)
    if (this.scorePanel) {
      this.scorePanel.setScale(scale);
      const pos = layout.getScorePanelPosition(scale, UI_CONFIG.PANELS.SCORE.HEIGHT);
      this.scorePanel.setPosition(pos.x, pos.y);
    }

    // Update Turret Count Panel (bottom-right)
    if (this.turretCountPanel) {
      this.turretCountPanel.setScale(scale);
      const pos = layout.getTurretCountPanelPosition(scale, UI_CONFIG.PANELS.TURRET_COUNT.WIDTH, UI_CONFIG.PANELS.TURRET_COUNT.HEIGHT);
      this.turretCountPanel.setPosition(pos.x, pos.y);
    }

    // Update Status Panel (bottom-center)
    if (this.statusPanel) {
      this.statusPanel.setScale(scale);
      const pos = layout.getStatusPanelPosition(scale, UI_CONFIG.PANELS.STATUS.WIDTH, UI_CONFIG.PANELS.STATUS.HEIGHT);
      this.statusPanel.setPosition(pos.x, pos.y);
    }

    // Update Mute Button (left column, first element)
    if (this.muteButton) {
      this.muteButton.setScale(scale);
      const pos = layout.getMuteButtonPosition(scale);
      this.muteButton.setPosition(pos.x, pos.y);
    }

    // Update Combat Stats Panel (left column, below mute button)
    if (this.combatStatsPanel) {
      this.combatStatsPanel.setScale(scale);
      const pos = layout.getCombatStatsPosition(scale);
      this.combatStatsPanel.setPosition(pos.x, pos.y);
    }

    // Update Turret Menu (right side)
    if (this.turretMenu) {
      this.turretMenu.container.scale.set(scale);
      const pos = layout.getTurretMenuPosition(scale, UI_CONFIG.PANELS.TURRET_MENU.WIDTH);
      this.turretMenu.container.position.set(pos.x, pos.y);
    }

    // Update Turret Upgrade Panel (left of turret menu)
    if (this.turretUpgradePanel) {
      this.turretUpgradePanel.container.scale.set(scale);
      const pos = layout.getTurretUpgradePanelPosition(scale, UI_CONFIG.PANELS.TURRET_MENU.WIDTH, UI_CONFIG.PANELS.UPGRADE.WIDTH);
      this.turretUpgradePanel.container.position.set(pos.x, pos.y);
    }

    // Update Mobile Controls
    if (this.mobileControls) {
      this.mobileControls.updateLayout(scale);
    }

    // Update Message Log
    if (this.messageLog) {
      this.messageLog.container.scale.set(scale);
      const pos = layout.getMessageLogPosition(scale, UI_CONFIG.MESSAGE_LOG.BOTTOM_OFFSET);
      this.messageLog.container.position.set(pos.x, pos.y);
    }

    // Update Toggle Buttons (left column, indexed positioning)
    // 0=God Mode, 1=Slow Mode, 2=AI, 3=AI Brain
    if (this.godModeButton) {
      this.godModeButton.setScale(scale);
      const pos = layout.getToggleButtonPosition(0, scale);
      this.godModeButton.setPosition(pos.x, pos.y);
    }

    if (this.slowModeButton) {
      this.slowModeButton.setScale(scale);
      const pos = layout.getToggleButtonPosition(1, scale);
      this.slowModeButton.setPosition(pos.x, pos.y);
    }

    if (this.aiButton) {
      this.aiButton.setScale(scale);
      const pos = layout.getToggleButtonPosition(2, scale);
      this.aiButton.setPosition(pos.x, pos.y);
    }

    if (this.aiBrainButton) {
      this.aiBrainButton.setScale(scale);
      const pos = layout.getToggleButtonPosition(3, scale);
      this.aiBrainButton.setPosition(pos.x, pos.y);
    }

    // Update AI Panel (below toggle buttons)
    if (this.aiPanel) {
      this.aiPanel.setScale(scale);
      const pos = layout.getAIPanelPosition(scale);
      this.aiPanel.setPosition(pos.x, pos.y);
    }

    // Update AI Thought Feed (below AI Panel)
    if (this.aiThoughtFeed) {
      const { height: aiPanelHeight } = AIPanel.getDimensions();
      this.aiThoughtFeed.setScale(scale);
      const pos = layout.getAIThoughtFeedPosition(scale, aiPanelHeight);
      this.aiThoughtFeed.setPosition(pos.x, pos.y);
    }
  }

  /**
   * Create top-left panel: Wave info using WavePanel component
   */
  private createTopLeftPanel(): void {
    const padding = UI_STYLES.PADDING;
    this.wavePanel = new WavePanel();
    this.wavePanel.init(this.container);
    this.wavePanel.setPosition(padding, padding);
  }

  /**
   * Create top-right panel: Resources display
   */
  private createTopRightPanel(): void {
    const padding = UI_STYLES.PADDING;
    const panelWidth = UI_CONFIG.PANELS.RESOURCE.WIDTH;
    const x = GAME_CONFIG.WORLD_WIDTH - panelWidth - padding;

    this.resourcePanel = new ResourcePanel();
    this.resourcePanel.init(this.container);
    this.resourcePanel.setPosition(x, padding);
  }

  /**
   * Create bottom-left panel: Score info using ScorePanel component
   */
  private createBottomLeftPanel(): void {
    const padding = UI_STYLES.PADDING;
    const y = GAME_CONFIG.WORLD_HEIGHT - UI_CONFIG.PANELS.SCORE.HEIGHT - padding;

    this.scorePanel = new ScorePanel();
    this.scorePanel.init(this.container);
    this.scorePanel.setPosition(padding, y);
  }

  /**
   * Create bottom-center panel: Kobayashi Maru status
   */
  private createBottomCenterPanel(): void {
    const panelWidth = UI_CONFIG.PANELS.STATUS.WIDTH;
    const panelHeight = UI_CONFIG.PANELS.STATUS.HEIGHT;
    const x = (GAME_CONFIG.WORLD_WIDTH - panelWidth) / 2;
    const y = GAME_CONFIG.WORLD_HEIGHT - panelHeight - UI_STYLES.PADDING;

    this.statusPanel = new StatusPanel();
    this.statusPanel.init(this.container);
    this.statusPanel.setPosition(x, y);
  }

  /**
   * Create bottom-right panel: Turret count using TurretCountPanel component
   */
  private createBottomRightPanel(): void {
    const padding = UI_STYLES.PADDING;
    const panelWidth = UI_CONFIG.PANELS.TURRET_COUNT.WIDTH;
    const x = GAME_CONFIG.WORLD_WIDTH - panelWidth - padding;
    const y = GAME_CONFIG.WORLD_HEIGHT - UI_CONFIG.PANELS.TURRET_COUNT.HEIGHT - padding;

    this.turretCountPanel = new TurretCountPanel();
    this.turretCountPanel.init(this.container);
    this.turretCountPanel.setPosition(x, y);
  }

  /**
   * Create sound mute button in the top-left corner below the wave panel
   * Uses IconButton component for consistent hover highlight behavior
   */
  private createMuteButton(): void {
    const padding = UI_STYLES.PADDING;
    const x = padding;
    const y = padding + 100 + padding;

    // Icon drawing function that handles speaker + X or sound waves
    const drawSpeakerIcon = (g: Graphics, active: boolean, colors: typeof UI_STYLES.COLORS) => {
      const iconColor = active ? colors.PRIMARY : 0x888888;

      // Speaker body (rectangle part)
      g.rect(0, 6, 6, 8);
      g.fill({ color: iconColor });

      // Speaker cone (triangle part)
      g.moveTo(6, 6);
      g.lineTo(12, 1);
      g.lineTo(12, 19);
      g.lineTo(6, 14);
      g.closePath();
      g.fill({ color: iconColor });

      if (active) {
        // Sound waves when active (not muted)
        g.arc(12, 10, 4, -0.7, 0.7, false);
        g.stroke({ color: iconColor, width: 2 });
        g.arc(12, 10, 7, -0.5, 0.5, false);
        g.stroke({ color: iconColor, width: 2 });
      } else {
        // X mark when muted
        g.moveTo(14, 4);
        g.lineTo(20, 16);
        g.stroke({ color: colors.DANGER, width: 2 });
        g.moveTo(20, 4);
        g.lineTo(14, 16);
        g.stroke({ color: colors.DANGER, width: 2 });
      }
    };

    this.muteButton = new IconButton({
      label: 'SOUND',
      activeColor: UI_STYLES.COLORS.PRIMARY,
      inactiveColor: 0x888888,
      onClick: () => {
        const audioManager = AudioManager.getInstance();
        audioManager.toggleMute();
        // Update the label based on new state
        const isMuted = audioManager.isMuted();
        this.muteButton?.setLabel(isMuted ? 'MUTED' : 'SOUND');
        this.muteButton?.updateVisualState();
      },
      isActive: () => {
        const audioManager = AudioManager.getInstance();
        return !audioManager.isMuted();
      },
      drawIcon: drawSpeakerIcon
    });

    this.muteButton.init(this.container);
    this.muteButton.setPosition(x, y);

    // Set initial label based on current mute state
    const audioManager = AudioManager.getInstance();
    const isMuted = audioManager.isMuted();
    this.muteButton.setLabel(isMuted ? 'MUTED' : 'SOUND');
    this.muteButton.updateVisualState();
  }

  /**
   * Create stats panel using CombatStatsPanel component
   */
  private createStatsPanel(): void {
    const padding = UI_STYLES.PADDING;
    const x = padding;
    const y = padding + 100 + padding + 40 + padding;

    this.combatStatsPanel = new CombatStatsPanel();
    this.combatStatsPanel.init(this.container);
    this.combatStatsPanel.setPosition(x, y);
  }

  /**
   * Create god mode toggle button using ToggleButton component
   */
  private createGodModeButton(): void {
    const padding = UI_STYLES.PADDING;
    const x = padding;
    const y = padding + 100 + padding + 40 + padding + 90 + padding;

    this.godModeButton = new ToggleButton({
      label: 'GOD MODE',
      enabledColor: UI_STYLES.COLORS.HEALTH,
      onClick: () => this.callbacks?.onToggleGodMode(),
      isEnabled: () => this.godModeEnabled
    });
    this.godModeButton.init(this.container);
    this.godModeButton.setPosition(x, y);
  }

  /**
   * Create slow mode toggle button using ToggleButton component
   */
  private createSlowModeButton(): void {
    const padding = UI_STYLES.PADDING;
    const toggleBtnHeight = ToggleButton.getDimensions().height;
    const x = padding;
    const y = padding + 100 + padding + 40 + padding + 90 + padding + toggleBtnHeight + padding;

    this.slowModeButton = new ToggleButton({
      label: 'SLOW MODE',
      enabledColor: UI_STYLES.COLORS.SECONDARY,
      onClick: () => this.callbacks?.onToggleSlowMode(),
      isEnabled: () => this.slowModeEnabled
    });
    this.slowModeButton.init(this.container);
    this.slowModeButton.setPosition(x, y);
  }

  /**
   * Create AI auto-play toggle button using ToggleButton component
   */
  private createAIButton(): void {
    const padding = UI_STYLES.PADDING;
    const toggleBtnHeight = ToggleButton.getDimensions().height;
    const x = padding;
    const y = padding + 100 + padding + 40 + padding + 90 + padding + toggleBtnHeight * 2 + padding * 2;

    this.aiButton = new ToggleButton({
      label: '🤖 AI AUTO',
      enabledColor: 0x00FF88,
      onClick: () => this.callbacks?.onToggleAI?.(),
      isEnabled: () => this.aiEnabled
    });
    this.aiButton.init(this.container);
    this.aiButton.setPosition(x, y);
  }

  /**
   * Create AI Brain toggle button for visualization overlays
   */
  private createAIBrainButton(): void {
    const padding = UI_STYLES.PADDING;
    const toggleBtnHeight = ToggleButton.getDimensions().height;
    const x = padding;
    const y = padding + 100 + padding + 40 + padding + 90 + padding + toggleBtnHeight * 3 + padding * 3;

    this.aiBrainButton = new ToggleButton({
      label: '👁 SEE AI BRAIN',
      enabledColor: 0x00FFFF, // Cyan for visibility
      onClick: () => {
        this.aiBrainEnabled = !this.aiBrainEnabled;

        // Toggle renderer visibility
        if (this.aiBrainRenderer) {
          this.aiBrainRenderer.setEnabled(this.aiBrainEnabled);
        }

        // Toggle panel expanded mode
        if (this.aiPanel) {
          this.aiPanel.setExpandedMode(this.aiBrainEnabled);
        }

        // Notify callback
        this.callbacks?.onToggleAIBrain?.();

        return this.aiBrainEnabled;
      },
      isEnabled: () => this.aiBrainEnabled
    });
    this.aiBrainButton.init(this.container);
    this.aiBrainButton.setPosition(x, y);
  }

  /**
   * Create AI Panel and Thought Feed for engagement display
   */
  private createAIPanel(): void {
    const padding = UI_STYLES.PADDING;

    // AI Panel - shows commander status, mood, phase
    // Positioned on left side, below the toggle buttons (now 4th slot for AI Brain button)
    const toggleBtnHeight = ToggleButton.getDimensions().height;
    this.aiPanel = new AIPanel();
    this.aiPanel.init(this.container);  // Now adds itself to parent
    const aiPanelDims = AIPanel.getDimensions();
    const aiPanelY = padding + 100 + padding + 40 + padding + 90 + padding + toggleBtnHeight * 4 + padding * 4 + padding;
    this.aiPanel.setPosition(padding, aiPanelY);
    this.aiPanel.hide(); // Hide until AI is enabled

    // AI Thought Feed - scrolling message log
    // Positioned below AI Panel on left side
    this.aiThoughtFeed = new AIThoughtFeed();
    this.aiThoughtFeed.init(this.container);  // Now adds itself to parent
    const feedY = aiPanelY + aiPanelDims.height + padding;
    this.aiThoughtFeed.setPosition(padding, feedY);
    this.aiThoughtFeed.hide(); // Hide until AI is enabled

    // Initialize AI Brain Renderer
    this.aiBrainRenderer = new AIBrainRenderer();
  }

  /**
   * Update AI HUD with extended status
   * @param status - Extended AI status from AIAutoPlayManager
   */
  updateAI(status: AIStatusExtended): void {
    if (!this.aiPanel || !this.aiThoughtFeed) return;

    // Show/hide AI panels based on enabled state
    if (status.enabled) {
      this.aiPanel.show();
      this.aiThoughtFeed.show();
      this.aiPanel.update(status);
      this.aiThoughtFeed.update();
    } else {
      this.aiPanel.hide();
      this.aiThoughtFeed.hide();
    }
  }

  /**
   * Update AI Brain visualization with current data
   * @param status - Extended AI status
   * @param threats - Current threat vectors
   * @param sectors - Coverage sector data
   */
  updateAIBrain(status: AIStatusExtended, threats: ThreatVector[], sectors: SectorData[]): void {
    if (!this.aiBrainRenderer || !this.aiBrainEnabled) return;

    this.aiBrainRenderer.setThreats(threats);
    this.aiBrainRenderer.setSectors(sectors);
    this.aiBrainRenderer.render(status);
  }

  /**
   * Get the AI Brain renderer container for adding to game stage
   */
  getAIBrainRenderer(): AIBrainRenderer | null {
    return this.aiBrainRenderer;
  }

  /**
   * Check if AI Brain visualization is enabled
   */
  isAIBrainEnabled(): boolean {
    return this.aiBrainEnabled;
  }

  /**
   * Add an AI message to the thought feed
   * @param message - Message to display
   */
  addAIMessage(message: AIMessage): void {
    if (this.aiThoughtFeed) {
      this.aiThoughtFeed.addMessage(message);
    }
  }

  /**
   * Update all HUD elements with new data
   * @param data - HUD data to display
   * @param deltaTime - Time since last frame in seconds
   */
  update(data: HUDData, deltaTime: number = 0): void {
    if (!this.visible) return;

    // Advance tick-based UI animations
    UIAnimator.tick(deltaTime);

    // Update wave panel
    if (this.wavePanel) {
      this.wavePanel.update({
        waveNumber: data.waveNumber,
        waveState: data.waveState,
        enemyCount: data.activeEnemies
      });
    }

    // Update resources
    if (this.resourcePanel) {
      this.resourcePanel.update({ resources: data.resources });
    }

    // Update score panel
    if (this.scorePanel) {
      this.scorePanel.update({
        timeSurvived: data.timeSurvived,
        enemiesDefeated: data.enemiesDefeated
      });
    }

    // Update Kobayashi Maru status bars
    if (this.statusPanel) {
      this.statusPanel.update({
        health: data.kobayashiMaruHealth,
        maxHealth: data.kobayashiMaruMaxHealth,
        shield: data.kobayashiMaruShield,
        maxShield: data.kobayashiMaruMaxShield
      });
    }

    // Update turret count
    if (this.turretCountPanel) {
      this.turretCountPanel.update({ turretCount: data.turretCount });
    }
    if (this.turretMenu) {
      this.turretMenu.update(data.resources);
    }

    // Update combat stats
    if (this.combatStatsPanel) {
      this.combatStatsPanel.update({
        dps: data.dps,
        accuracy: data.accuracy,
        totalDamageDealt: data.totalDamageDealt
      });
    }

    // Update cheat mode states
    if (data.godModeEnabled !== undefined) {
      this.godModeEnabled = data.godModeEnabled;
    }
    if (data.slowModeEnabled !== undefined) {
      this.slowModeEnabled = data.slowModeEnabled;
    }

    // Update AI enabled state
    if (data.aiEnabled !== undefined) {
      this.aiEnabled = data.aiEnabled;
    }

    // Update message log fade effect
    if (this.messageLog) {
      this.messageLog.update();
    }

    // Update combo panel popup animation
    if (this.comboPanel) {
      this.comboPanel.update(deltaTime);
    }

    // Update wave announcement animation
    if (this.waveAnnouncement) {
      this.waveAnnouncement.update(deltaTime);
    }

    // Update alert status overlay animation
    if (this.alertStatusOverlay) {
      this.alertStatusOverlay.update(deltaTime);
    }
  }

  /**
   * Show the HUD
   */
  show(): void {
    this.visible = true;
    this.container.visible = true;
  }

  /**
   * Hide the HUD
   */
  hide(): void {
    this.visible = false;
    this.container.visible = false;
  }

  /**
   * Check if HUD is currently visible
   */
  isVisible(): boolean {
    return this.visible;
  }

  /**
   * Clean up all resources
   */
  destroy(): void {
    // Clean up panel components
    if (this.wavePanel) this.wavePanel.destroy();
    if (this.resourcePanel) this.resourcePanel.destroy();
    if (this.statusPanel) this.statusPanel.destroy();
    if (this.scorePanel) this.scorePanel.destroy();
    if (this.turretCountPanel) this.turretCountPanel.destroy();
    if (this.combatStatsPanel) this.combatStatsPanel.destroy();
    if (this.mobileControls) this.mobileControls.destroy();
    if (this.messageLog) this.messageLog.destroy();
    if (this.turretUpgradePanel) this.turretUpgradePanel.destroy();
    if (this.godModeButton) this.godModeButton.destroy();
    if (this.muteButton) this.muteButton.destroy();
    if (this.slowModeButton) this.slowModeButton.destroy();
    if (this.aiButton) this.aiButton.destroy();
    if (this.aiBrainButton) this.aiBrainButton.destroy();
    if (this.aiPanel) this.aiPanel.destroy();
    if (this.aiThoughtFeed) this.aiThoughtFeed.destroy();
    if (this.aiBrainRenderer) this.aiBrainRenderer.destroy();
    if (this.comboPanel) this.comboPanel.destroy();
    if (this.waveAnnouncement) this.waveAnnouncement.destroy();
    if (this.alertStatusOverlay) this.alertStatusOverlay.destroy();

    // Remove resize listener
    if (this.boundResizeHandler) {
      window.removeEventListener('resize', this.boundResizeHandler);
    }

    // Destroy container
    this.container.destroy({ children: true });
  }

  /**
   * Get the turret menu instance
   */
  getTurretMenu(): TurretMenu | null {
    return this.turretMenu;
  }

  /**
   * Get the turret upgrade panel instance
   */
  getTurretUpgradePanel(): TurretUpgradePanel | null {
    return this.turretUpgradePanel;
  }

  /**
   * Add a message to the message log
   * @param text - Message text to display
   * @param category - Optional message category
   */
  addLogMessage(text: string, category?: 'info' | 'warning' | 'damage' | 'kill' | 'wave'): void {
    if (this.messageLog) {
      this.messageLog.addMessage(text, category);
    }
  }
}
